/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 * NavbarService layer between component and Content provider service to handle all filtering
 * and data manipulation logic for global navigation data
 */

// It is not considering import as declaration , throwing lint error at import statement itself

import { EventEmitter, Injectable } from '@angular/core';
import { ContentProviderService } from '@core/aem/services/content-provider.service'; // @TODO remove
import { MatchmediaService } from '@core/services/matchmedia.service';
import { Link } from '../models/link.interface';
import { Observable } from 'rxjs';
import { UserService } from '@support/shared/services/user.service';
import { HeaderPrimaryNav, ModuleEnhanceMentsNavbar } from '../models/header-primary-nav.interface';
import { FooterModuleEnhancements } from '../models/footer.interface';
import { SETTINGS } from '@environment/settings';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { map } from 'rxjs/operators';
/**
 * Service layer between component and Content provider service to handle all filtering
 * and data manipulation logic for global navigation data
 * @docs-private
 */
@Injectable()
export class NavbarService {
  /**
   * Static AEM data.
   */
  public navigationData: HeaderPrimaryNav;
  /**
   * Boolean to store if device width is more than 992px.
   */
  public navbarChangeEmitter: EventEmitter<{ id: string }>;
  /**
   * Boolean to store if user is logged in.
   * TODO: it should listen to global login listener
   */
  private loggedIn: boolean;
  /**
   * Stores the boolean value for the desktop
   */
  private desktop: boolean;
  /**
   * Stores the boolean value for the secondary nav expanded
   */
  private secondaryNavExpanded: boolean;

  /**
   * Emits whether tourist plan is present in cart
   */
  public touristPlanEmitter: EventEmitter<boolean>;

  /**
   * Emits whether tourist plan is present in cart
   */
  public permissionsUpdated: EventEmitter<boolean>;

  /**
   * emitter to hide the navigation links
   */
  public hideGlobalNavEmitter: EventEmitter<boolean>;
  public isP10MCustomer: boolean;
  constructor(
    private contentProviderService: ContentProviderService,
    private matchMediaService: MatchmediaService,
    private userService: UserService
  ) {
    this.navbarChangeEmitter = new EventEmitter();
    this.touristPlanEmitter = new EventEmitter<boolean>();
    this.permissionsUpdated = new EventEmitter<boolean>();
    this.hideGlobalNavEmitter = new EventEmitter<boolean>();
    this.userService.infoChangeEmitter.subscribe(
      (event: { customerInfo: object; customerAccountInfo: object; loggedOut: boolean }) => {
        if (event.customerInfo || event.customerAccountInfo) {
          this.loggedIn = true;
        } else if (event.loggedOut) {
          this.loggedIn = false;
          if (this.isP10MCustomer) {
            this.userService.p10CustomerLoggedIn.next(false);
          }
          this.navbarChangeEmitter.emit({ id: 'loggedOut' });
        }
      }
    );
    this.userService.p10CustomerLoggedIn$.subscribe(
      (data: boolean) => (this.isP10MCustomer = data)
    );
    this.userService.infoChangeSubject.subscribe((event: { isGuestAccount: boolean }) => {
      this.loggedIn = !event.isGuestAccount;
      if (!event.isGuestAccount) {
        this.navbarChangeEmitter.emit({ id: 'loggedIn' });
      } else {
        if (this.isP10MCustomer) {
          this.userService.p10CustomerLoggedIn.next(false);
        }
        this.navbarChangeEmitter.emit({ id: 'loggedOut' });
      }
    });
  }

  /**
   * Returns current value if customer data exists
   * @returns boolean
   */
  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  /**
   * Sets the isLoggedIn value
   * @param value : stores the boolean value
   * @returns void
   */
  public setIsLoggedIn(value: boolean): void {
    this.loggedIn = value;
  }

  /**
   * Function sets the expand secondary nav value to true and emits change event
   * @returns void
   */
  public expandSecondaryNav(): void {
    this.secondaryNavExpanded = true;
    this.navbarChangeEmitter.emit({ id: 'secondaryNavExpanded' });
  }
  /**
   * Function sets the expand secondary nav value to false and emits change event
   * @returns void
   */
  public collapseSecondaryNav(): void {
    this.secondaryNavExpanded = false;
    this.navbarChangeEmitter.emit({ id: 'secondaryNavExpanded' });
  }
  /**
   * Returns boolean value of secondaryNavExpanded
   * @returns boolean
   */
  public isSecondaryNavExpanded(): boolean {
    return this.secondaryNavExpanded;
  }

  /**
   * Makes call to content-provider-service to fetch AEM data.
   * @param pageUrl String dynamic part of AEM call.
   * @returns ModuleEnhanceMentsNavbar
   */
  public getContentData(
    pageUrl: string
  ): Observable<ModuleEnhanceMentsNavbar | FooterModuleEnhancements> {
    return this.contentProviderService.getContentData(pageUrl).pipe(
      map((contentData: ModuleEnhanceMentsNavbar | FooterModuleEnhancements) => {
        return contentData;
      })
    );
  }

  /**
   * Getter for returning a boolean value, if device width is more than 992px.
   * @returns boolean
   */
  public isDesktop(): boolean {
    this.desktop = this.matchMediaService.isDesktop();
    return this.desktop;
  }
  /**
   * Filters the links on the basis of breakpoint and user logged instate.
   * @param links - Array of links object
   * @returns Link[]
   */
  public getFilteredLinks(links: Link[]): Link[] {
    if (links === null) {
      return;
    }
    return links.filter((link: Link) => this.showMenuLink(link));
  }

  /**
   * Used to remove the customer data from state store when he logged out
   */
  public removeCustomerData(customerId: string): void {
    this.userService.removeUserData(customerId);
  }
  /**
   * Checks if menu link is to be displayed on not on the basis of breakpoint and user logged in state.
   * @param menuLink - Link object
   * @returns boolean
   */
  private showMenuLink(menuLink: Link): boolean {
    let showOnBreakPoint: boolean;
    let authCheck: boolean;
    let assistedCheck: boolean;
    let checkP10M: boolean;
    let connectView: boolean;
    showOnBreakPoint = this.handleShowOnBreakPoint(menuLink.breakPoints);
    authCheck = this.handleAuthCheck(menuLink.showAuth, menuLink.showUnAuth);
    assistedCheck = this.handleAssistedCheck(menuLink);
    showOnBreakPoint = this.handleChannelSpecificLogic(menuLink.url, showOnBreakPoint);
    connectView = this.handleMenuConnectPrepaid(menuLink);

    checkP10M = this.isP10MCustomer ? menuLink.showP10M : !!menuLink.showNonP10M;
    return (
      showOnBreakPoint &&
      authCheck &&
      assistedCheck &&
      checkP10M &&
      connectView &&
      (!menuLink.channel || menuLink.channel.includes(SETTINGS.CHANNEL.toLocaleLowerCase()))
    );
  }

  private handleAuthCheck(showAuth: boolean, showUnAuth: boolean): boolean {
    return this.loggedIn ? showAuth : showUnAuth;
  }

  private handleShowOnBreakPoint(breakPoints: string[]): boolean {
    return this.isDesktop()
      ? breakPoints.indexOf('desktop') > -1
      : breakPoints.indexOf('tablet') > -1 || breakPoints.indexOf('mobile') > -1;
  }
  private handleChannelSpecificLogic(url: string, showOnBreakPoint: boolean): boolean {
    let isShowOnBreakPoint = showOnBreakPoint;
    // if (!(this.loggedIn) &&
    //   url === SHARED_CONSTANTS.DIRECT_TO_ACCOUNT_LINK &&
    //   SETTINGS.CHANNEL.toLocaleLowerCase() === 'retail') {
    //   isShowOnBreakPoint = false;
    // }

    // if (!(this.loggedIn) &&
    //   url === SHARED_CONSTANTS.REDEEM_GUEST &&
    //   SETTINGS.CHANNEL.toLocaleLowerCase() !== 'retail') {
    //   isShowOnBreakPoint = false;
    // }

    if (
      this.loggedIn &&
      (url === SHARED_CONSTANTS.DIRECT_TO_ACCOUNT_LINK || url === SHARED_CONSTANTS.REDEEM_GUEST)
    ) {
      isShowOnBreakPoint = false;
    }

    return isShowOnBreakPoint;
  }

  private handleAssistedCheck(menuLink: Link): boolean {
    let isAssistedCheck: boolean;
    // if (SETTINGS.CHANNEL.toLocaleLowerCase() === 'web' &&  menuLink.showUnAssisted !== undefined) {
    if (menuLink.showUnAssisted !== undefined) {
      isAssistedCheck = menuLink.showUnAssisted === true;
    }
    // else if (SETTINGS.CHANNEL.toLocaleLowerCase() !== 'web' && menuLink.showAssisted !== undefined) {
    //   isAssistedCheck = menuLink.showAssisted === true;
    // }
    else {
      isAssistedCheck = true;
    }
    return isAssistedCheck;
  }

  private handleMenuConnectPrepaid(menuLink: Link): boolean {
    let isConnectView: boolean;
    if (menuLink.showConnect !== undefined && document.cookie.includes('brand=connect')) {
      isConnectView = menuLink.showConnect;
      return isConnectView;
    }
    if (menuLink.label === 'Why Connect') {
      return false;
    }
    return menuLink.label === ('Plans' || 'Coverage') ? !menuLink.showConnect : true;
  }
}
